// src/components/Performance/PerformanceTable.tsx

import React, { useState } from "react";
import { Table, Empty, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Calendar } from "lucide-react";
import { feedbackConfig, mapTypeToConfigKey } from "./performanceHelper";
import DeletePerformanceNoteModal from "./DeletePerformanceNoteModal";
import AddNoteModal from "./AddNoteModal";

interface PerformanceNote {
  employeePerformanceNotesId: string;
  type: string;
  title: string;
  description: string;
  nextSteps?: string;
  createdDate?: string;
  createdOn?: string;
  adminName: string;
  adminRole: string;
  reviewStatus?: string;
  employeeId: string;
}

interface TableViewProps {
  performanceData?: PerformanceNote[];
  loading: boolean;
  refreshData: () => void;
  pastEmployee: boolean;
}

const TableView: React.FC<TableViewProps> = ({ performanceData, loading, refreshData, pastEmployee }) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedNoteForDelete, setSelectedNoteForDelete] = useState<PerformanceNote | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedNoteForEdit, setSelectedNoteForEdit] = useState<PerformanceNote | null>(null);

  const openDeleteModal = (record: PerformanceNote) => {
    setSelectedNoteForDelete(record);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedNoteForDelete(null);
    setDeleteModalOpen(false);
  };

  const openEditModal = (record: PerformanceNote) => {
    setSelectedNoteForEdit(record);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedNoteForEdit(null);
  };

  const columns: ColumnsType<PerformanceNote> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 140,
      align: "center",
      render: (text: string) => <span className="text-[14px] text-gray-700 break-words">{text && text.trim() !== "" ? text : "-"}</span>,
    },

    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      align: "center",
      render: (text: string) => {
        if (!text) {
          return <div className="text-[14px] text-gray-700 leading-6 ml-2">-</div>;
        }

        const shouldTruncate = text.length > 30;
        const displayText = shouldTruncate ? text.slice(0, 30) + "..." : text;

        return shouldTruncate ? (
          <Tooltip title={text} placement="topLeft">
            <div className="text-[14px] text-gray-700 leading-6">{displayText}</div>
          </Tooltip>
        ) : (
          <div className="text-[14px] text-gray-700 leading-6">{displayText}</div>
        );
      },
    },

    {
      title: "Date",
      dataIndex: "createdOn",
      key: "date",
      width: 150,
      align: "center",
      render: (_: any, record: { createdOn: any }) => {
        const dateValue = record.createdOn;
        return dateValue ? (
          <div className="flex items-center justify-center gap-1 text-[14px] text-gray-600 whitespace-nowrap">
            <Calendar size={14} className="text-gray-500" />
            {new Date(dateValue).toLocaleDateString("en-GB")}
          </div>
        ) : (
          <div className="text-gray-400 text-[14px] w-full text-center">-</div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 200,
      align: "center",
      render: (type: string) => {
        const configKey = mapTypeToConfigKey(type);
        const config = feedbackConfig[configKey];
        const Icon = config.icon;
        return (
          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[14px] ${config.badgeColor} whitespace-nowrap justify-center`}>
            {Icon && <Icon size={14} />}
            {config.label}
          </span>
        );
      },
    },
    {
      title: "Next Steps",
      dataIndex: "nextSteps",
      key: "nextSteps",
      width: 200,
      align: "center",
      render: (steps: string) => {
        if (!steps) {
          return (
            <div className="text-gray-400 text-[14px] w-full ml-1 leading-6">
              <div>-</div>
            </div>
          );
        }

        const content = <div className="text-[14px] text-gray-700 whitespace-pre-line break-words leading-6 ">{steps.length > 30 ? steps.slice(0, 30) + "..." : steps}</div>;

        return steps.length > 20 ? (
          <Tooltip placement="top" title={steps}>
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    },

    !pastEmployee && {
      title: "Action",
      key: "action",
      width: 100,
      align: "center",
      render: (_: any, record: PerformanceNote) => {
        const isDraft = record.reviewStatus === "Draft";

        return (
          <div className="flex items-center justify-center gap-2">
            <Tooltip placement="top" title={isDraft ? "Edit" : ""}>
              <span
                className={`material-symbols-outlined ${isDraft ? "cursor-pointer hover:text-primary" : "opacity-0 pointer-events-none"}`}
                onClick={() => isDraft && openEditModal(record)}
                aria-label="Edit Note"
              >
                edit
              </span>
            </Tooltip>

            <Tooltip placement="top" title="Delete">
              <span className="material-symbols-outlined cursor-pointer hover:text-red-600" onClick={() => openDeleteModal(record)} aria-label="Delete Note">
                delete
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ].filter(Boolean) as ColumnsType<PerformanceNote>;

  return (
    <div className="pr-2">
      <div className="flex-1 ">
        <Table
          className="text-[14px] text-gray-700 "
          columns={columns}
          dataSource={performanceData}
          loading={loading}
          rowKey="employeePerformanceNotesId"
          pagination={false}
          size="middle"
          locale={{
            emptyText: (
              <div className="custom-empty-placeholder">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}

                  description={
                    <span className="text-[14px] text-gray-700 font-medium">
                      No performance notes found.
                    </span>
                  }
                />
              </div>
            ),
          }}




        />
      </div>

      {/* Delete Modal */}
      {selectedNoteForDelete && (
        <DeletePerformanceNoteModal
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          employeeId={selectedNoteForDelete.employeeId}
          noteId={selectedNoteForDelete.employeePerformanceNotesId}
          refreshData={refreshData}
        />
      )}

      {/* Edit Modal */}
      <AddNoteModal
        open={editModalOpen}
        onCancel={closeEditModal}
        employeeId={Number(selectedNoteForEdit?.employeeId)}
        isEdit={true}
        noteData={selectedNoteForEdit}
        refreshData={() => {
          refreshData();
          closeEditModal();
        }}
      />
    </div>
  );
};

export default TableView;
